/* eslint-disable react-refresh/only-export-components */
import { useQuery } from '@tanstack/react-query'
import {
  createRouter,
  createRoute,
  createRootRoute,
  Navigate,
  Outlet,
  useRouterState,
} from '@tanstack/react-router'
import { api } from '@/lib/api'
import { BottomNav } from '@/components/BottomNav'
import { ServicesPage } from '@/pages/ServicesPage'
import { AuctionsPage } from '@/pages/AuctionsPage'
import { AuctionPage } from '@/pages/AuctionPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { CartPage } from '@/pages/CartPage'
import { BookingsPage } from '@/pages/BookingsPage'
import { LoginPage } from '@/pages/LoginPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { ProfileEditPage } from '@/pages/ProfileEditPage'
import { CheckoutPage } from '@/pages/CheckoutPage'
import { CatalogPage } from '@/pages/CatalogPage'
import { HomePage } from '@/pages/HomePage'
import { HomePage as MayparfumHomePage } from '@/sites/mayparfum/HomePage'
import { HomePage as KlodchikHomePage } from '@/sites/klodchik/HomePage'
import { HomePage as GnezdyshkoHomePage } from '@/sites/gnezdyshko/HomePage'
import { isLodgingShop } from '@/lib/format'
import { ProductPage } from '@/pages/ProductPage'
import { QuizPage } from '@/pages/QuizPage'
import { StaticPagePage } from '@/pages/StaticPagePage'
import { StoriesPage } from '@/pages/StoriesPage'
import { WishlistPage } from '@/pages/WishlistPage'
import { BrandsPage } from '@/pages/BrandsPage'
import { BrandPage } from '@/pages/BrandPage'
import { SpecShowcasePage } from '@/pages/SpecShowcasePage'
import { SandboxPaymentPage } from '@/pages/SandboxPaymentPage'
import { ScentFinderPage } from '@/pages/ScentFinderPage'
import { SeoHead } from '@/components/SeoHead'
import { ServerSeoContext } from '@/lib/seo'

interface PublicSettings {
  home_page_enabled: boolean
  shop_name: string
}

// `/` renders the marketing home page only when the shop opted in
// (`home_page_enabled`); every existing shop that never configured a
// hero keeps the legacy behaviour — straight to `/catalog` — so this
// ships without touching any bot that doesn't ask for it.
function IndexRoute() {
  const { data: settings, isLoading } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })
  if (isLoading) return null
  if (settings?.home_page_enabled) {
    const shopSlug = (settings?.shop_name ?? '').toLowerCase().replace(/\s+/g, '')
    if (shopSlug.includes('mayparfum')) {
      return <MayparfumHomePage />
    }
    if (shopSlug.includes('klodchik')) {
      return <KlodchikHomePage />
    }
    if (isLodgingShop(settings?.shop_name)) {
      return <GnezdyshkoHomePage />
    }
    return <HomePage />
  }
  return <Navigate to="/catalog" replace />
}

// ─── Root layout (wraps every page with BottomNav) ───────────────

function RootLayout() {
  const { location } = useRouterState()
  const isLogin = location.pathname.startsWith('/login')
  const isWidePublic =
    location.pathname === '/' ||
    location.pathname === '/catalog' ||
    location.pathname === '/brands' ||
    location.pathname.startsWith('/brands/') ||
    location.pathname.startsWith('/brand/') ||
    location.pathname.startsWith('/pages/')
  return (
    <ServerSeoContext.Provider value={true}>
      <SeoHead />
      <div className={`app-shell flex min-h-screen flex-col overflow-hidden ${isWidePublic ? 'app-shell-wide' : ''}`}>
        <main className={isLogin ? 'flex-1' : 'flex-1 pb-16'}>
          <Outlet />
        </main>
        {!isLogin && <BottomNav />}
      </div>
    </ServerSeoContext.Provider>
  )
}

const rootRoute = createRootRoute({ component: RootLayout })

// ─── Routes ─────────────────────────────────────────────────────

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const catalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog',
  component: CatalogPage,
  // Two independent deep-link shapes into /catalog:
  // - `?category=3` from the home page's category rail — preselects
  //   that category's filter chip.
  // - `?attr=X&value=Y` from a product's attribute tab — CatalogPage
  //   reads these off the raw URL itself (`includeFromUrl`), not off
  //   this typed search, but the shape still has to be declared here
  //   so `<Link search={{ attr, value }}>` type-checks.
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: number; attr?: string; value?: string; q?: string } => ({
    category: search.category !== undefined ? Number(search.category) : undefined,
    attr: typeof search.attr === 'string' ? search.attr : undefined,
    value: typeof search.value === 'string' ? search.value : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
})

const wishlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/wishlist',
  component: WishlistPage,
})

const productRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/$productSlug',
  component: ProductPage,
})

const productDirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/product/$productSlug',
  component: ProductPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
})

const quizRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/quiz',
  component: QuizPage,
})

const staticPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pages/$slug',
  component: StaticPagePage,
})

const servicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/services',
  component: ServicesPage,
})

const storiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/stories',
  component: StoriesPage,
})

const auctionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auctions',
  component: AuctionsPage,
})

const auctionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auctions/$auctionId',
  component: AuctionPage,
})

const cartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cart',
  component: CartPage,
})

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
})

const bookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings',
  component: BookingsPage,
})

// Room types now live as bookable cards inside `/catalog` (folded in
// alongside regular products) instead of a dedicated page — this route
// stays registered only so old bookmarks/links to `/lodging` still land
// somewhere useful.
const lodgingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lodging',
  component: () => <Navigate to="/catalog" replace />,
})

// ─── Router ─────────────────────────────────────────────────────

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: CheckoutPage,
})

const brandsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brands',
  component: BrandsPage,
})

const brandRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brands/$brandSlug',
  component: BrandPage,
})

const brandDirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brand/$brandSlug',
  component: BrandPage,
})

const specShowcaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/specs/$specCode/$valueSlug',
  component: SpecShowcasePage,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
})

const profileEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile/edit',
  component: ProfileEditPage,
})

const sandboxPayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay/sandbox',
  component: SandboxPaymentPage,
})

interface ScentFinderSearch {
  tab?: 'compass' | 'matrix' | 'radar' | 'pyramid' | 'versus' | 'capsule'
}

const scentFinderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/scent-finder',
  validateSearch: (search: Record<string, unknown>): ScentFinderSearch => ({
    tab: typeof search.tab === 'string' ? (search.tab as ScentFinderSearch['tab']) : undefined,
  }),
  component: ScentFinderPage,
})

const compassRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compass',
  validateSearch: (search: Record<string, unknown>): ScentFinderSearch => ({
    tab: typeof search.tab === 'string' ? (search.tab as ScentFinderSearch['tab']) : undefined,
  }),
  component: ScentFinderPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  catalogRoute,
  productRoute,
  productDirectRoute,
  brandsRoute,
  brandRoute,
  brandDirectRoute,
  specShowcaseRoute,
  wishlistRoute,
  servicesRoute,
  storiesRoute,
  auctionsRoute,
  auctionRoute,
  cartRoute,
  checkoutRoute,
  ordersRoute,
  bookingsRoute,
  lodgingRoute,
  profileRoute,
  profileEditRoute,
  sandboxPayRoute,
  quizRoute,
  scentFinderRoute,
  compassRoute,
  staticPageRoute,
])


export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  basepath: '/app',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
